import { useEffect, useState } from "react";
import { Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useApi } from "../../hooks/useApi";
import { web, errMsg } from "../../lib/api";
import { LEVELS, POSITIONS, PCTS, LOWER_BETTER, benchmarkLabel } from "../../lib/benchmarks";
import { METRICS } from "../../components/app/CombinePanel";
import { PageHeader, Panel, Loader, ErrorNote, Empty, Btn, fmt, shortDate } from "../../components/app/kit";
import { Field, TextInput, TextArea, Chips, numOrNull } from "../../components/app/Form";

const blank = { metrics: {}, pcts: {}, source_name: "", source_year: "", notes: "" };
const strs = (o) => Object.fromEntries(Object.entries(o ?? {}).map(([k, v]) => [k, String(v)]));
const toForm = (b) => ({ metrics: strs(b.metrics), pcts: Object.fromEntries(Object.entries(b.percentiles ?? {}).map(([k, c]) => [k, strs(c)])), source_name: b.source_name, source_year: String(b.source_year), notes: b.notes ?? "" });
const nums = (o) => Object.fromEntries(Object.entries(o ?? {}).map(([k, v]) => [k, numOrNull(v)]).filter(([, v]) => v != null));
const cell = "w-full min-w-[64px] rounded-lg border border-[#1E293B] bg-[#0F172A] px-2 py-1.5 font-mono-gd text-sm text-white outline-none placeholder:text-slate-600 focus:border-[#39FF14]";

const MetricGrid = ({ f, setF }) => {
  const setM = (k) => (e) => setF((x) => ({ ...x, metrics: { ...x.metrics, [k]: e.target.value } }));
  const setP = (k, p) => (e) => setF((x) => ({ ...x, pcts: { ...x.pcts, [k]: { ...(x.pcts[k] ?? {}), [p]: e.target.value } } }));
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[600px] text-left text-sm" data-testid="bm-metric-grid">
        <thead><tr className="font-mono-gd text-[11px] uppercase tracking-wider text-slate-400">
          <th className="py-2 pr-2">Metric</th><th className="px-1 py-2">Average</th>{PCTS.map(([p, l]) => <th key={p} className="px-1 py-2">{l} pct</th>)}
        </tr></thead>
        <tbody>
          {METRICS.map(([k, label, unit]) => (
            <tr key={k} className="border-t border-[#1E293B]">
              <td className="py-1.5 pr-2 text-slate-200">{label}{unit && <span className="text-slate-400"> ({unit})</span>}{LOWER_BETTER.has(k) && <span className="block font-mono-gd text-[10px] uppercase text-slate-400">lower is better</span>}</td>
              <td className="px-1 py-1.5"><input data-testid={`bm-metric-${k}`} type="number" step="0.1" min="0" value={f.metrics[k] ?? ""} onChange={setM(k)} placeholder="—" className={cell} /></td>
              {PCTS.map(([p]) => <td key={p} className="px-1 py-1.5"><input data-testid={`bm-pct-${k}-${p}`} type="number" step="0.1" min="0" value={f.pcts[k]?.[p] ?? ""} onChange={setP(k, p)} placeholder="—" className={cell} /></td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const Editor = ({ tables, level, position, setLevel, setPosition, onSaved }) => {
  const existing = tables.find((b) => b.level === level && (b.position ?? "all") === position);
  const [f, setF] = useState(blank);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setF(existing ? toForm(existing) : blank); }, [existing?.id, level, position]); // eslint-disable-line react-hooks/exhaustive-deps
  const save = async () => {
    const metrics = nums(f.metrics);
    const percentiles = Object.fromEntries(Object.entries(f.pcts).map(([k, c]) => [k, nums(c)]).filter(([, c]) => Object.keys(c).length));
    if (!Object.keys(metrics).length && !Object.keys(percentiles).length) return toast.error("Enter at least one benchmark value");
    if (f.source_name.trim().length < 2 || !numOrNull(f.source_year)) return toast.error("Add the source name and year");
    setBusy(true);
    try {
      await web.put("/admin/benchmarks", { level, position: position === "all" ? null : position, metrics, percentiles, source_name: f.source_name.trim(), source_year: Number(f.source_year), notes: f.notes.trim() || null });
      toast.success("Benchmark saved");
      onSaved();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Panel data-testid="benchmark-editor">
      <h3 className="mb-1 font-display text-xl font-bold uppercase text-white">{existing ? "Edit" : "New"} benchmark table</h3>
      <p className="mb-5 text-sm text-slate-400">Players are matched by team level (Varsity or JV) and their first position. A position table wins over an all-positions table.</p>
      <div className="space-y-4">
        <Field label="Level"><Chips options={LEVELS} value={level} onChange={setLevel} testid="bm-level" /></Field>
        <Field label="Position"><Chips options={POSITIONS} value={position} onChange={setPosition} testid="bm-pos" /></Field>
        <div>
          <MetricGrid f={f} setF={setF} />
          <p className="mt-2 text-xs text-slate-400">Percentile cutoffs are optional. The 90th percentile is the result only the top 10% reach. Rankings like "Top 25%" only show for metrics with cutoffs.</p>
        </div>
        <div className="grid gap-3 sm:grid-cols-[1fr_140px]">
          <Field label="Source name (required)"><TextInput data-testid="bm-source-name" value={f.source_name} onChange={(e) => setF((x) => ({ ...x, source_name: e.target.value }))} placeholder="e.g. State association combine report" /></Field>
          <Field label="Source year"><TextInput data-testid="bm-source-year" type="number" value={f.source_year} onChange={(e) => setF((x) => ({ ...x, source_year: e.target.value }))} placeholder="2026" /></Field>
        </div>
        <Field label="Notes (optional, shown under the table)"><TextArea data-testid="bm-notes" value={f.notes} onChange={(e) => setF((x) => ({ ...x, notes: e.target.value }))} placeholder="Sample size, age range, how it was measured" /></Field>
        <Btn data-testid="bm-save" onClick={save} disabled={busy}>{busy ? "Saving..." : "Save benchmark"}</Btn>
      </div>
    </Panel>
  );
};

const TableCard = ({ b, onEdit, onDelete }) => (
  <li data-testid={`bm-table-${b.id}`} className="rounded-2xl border border-[#1E293B] bg-[#0F172A] p-4">
    <div className="flex items-start gap-3">
      <div className="min-w-0 flex-1">
        <p className="font-display text-lg font-bold uppercase text-white">{benchmarkLabel(b)}</p>
        <p className="text-sm text-slate-300">{b.source_name} ({b.source_year})</p>
        <p className="mt-1 font-mono-gd text-[11px] uppercase tracking-wider text-slate-400">{Object.keys(b.metrics ?? {}).length} averages · {Object.keys(b.percentiles ?? {}).length} with percentiles · updated {shortDate(b.updated_at)}</p>
      </div>
      <button data-testid={`bm-edit-${b.id}`} onClick={onEdit} aria-label="Edit" className="rounded-full p-2 text-slate-400 hover:text-[#39FF14]"><Pencil className="h-4 w-4" /></button>
      <button data-testid={`bm-delete-${b.id}`} onClick={onDelete} aria-label="Delete" className="rounded-full p-2 text-slate-400 hover:text-red-200"><Trash2 className="h-4 w-4" /></button>
    </div>
    <p className="mt-3 font-mono-gd text-xs text-slate-300">
      {METRICS.filter(([k]) => b.metrics?.[k] != null).map(([k, l, u]) => `${l} ${fmt(b.metrics[k])}${u}`).join(" · ")}
    </p>
  </li>
);

export default function Benchmarks() {
  const list = useApi("/benchmarks", web);
  const tables = Array.isArray(list.data) ? list.data : [];
  const [level, setLevel] = useState("varsity");
  const [position, setPosition] = useState("all");
  const remove = async (b) => {
    if (!window.confirm(`Delete the ${benchmarkLabel(b)} benchmark?`)) return;
    try { await web.delete(`/admin/benchmarks/${b.id}`); toast.success("Benchmark deleted"); list.reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div>
      <PageHeader eyebrow="Admin" title="Combine benchmarks" />
      <p className="mb-8 max-w-2xl text-sm text-slate-300">Enter average combine results for a level so developing players get a fair comparison. Only enter numbers from a real source; the source name and year are shown under every comparison.</p>
      <div className="grid gap-6 xl:grid-cols-[1.4fr_1fr]">
        <Editor tables={tables} level={level} position={position} setLevel={setLevel} setPosition={setPosition} onSaved={list.reload} />
        <div>
          <h3 className="mb-4 font-display text-xl font-bold uppercase text-white">Saved tables</h3>
          {list.loading ? <Loader /> : list.error ? <ErrorNote error={list.error} /> : !tables.length ? <Empty title="No benchmarks yet" text="Combine tabs use the NBA Draft Combine average until you add one." /> : (
            <ul className="space-y-3">
              {tables.map((b) => <TableCard key={b.id} b={b} onEdit={() => { setLevel(b.level); setPosition(b.position ?? "all"); window.scrollTo({ top: 0, behavior: "smooth" }); }} onDelete={() => remove(b)} />)}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
